import { addDaysToDate, getDateDifference } from '@travelata-libs/utils';
import { MAX_NIGHTS, MIN_NIGHTS } from '@/shared/config/nights';
import type { DateRange, NightsRange } from '@/shared/model';
import {
  clampDate,
  clampFlexible,
  getMaxDate,
  getMinDate,
  MAX_FLEXIBLE,
  normalizeDateFrom,
  parseDate,
  toISO,
} from './dateRange';
import type { StayCriteria, StayDates } from '../model/types';

const DEFAULT_STAY_NIGHTS = 7;

export function countNights(checkInIso: string, checkOutIso: string): number {
  return getDateDifference(parseDate(checkOutIso), parseDate(checkInIso), 'days');
}

export function getCheckOut(stay: StayDates): string {
  return toISO(addDaysToDate(parseDate(stay.checkIn), stay.nights));
}

function getStayNights(nights: NightsRange | null): number {
  const lowerBound = Math.round(Number(nights?.from));
  if (!lowerBound) {
    return DEFAULT_STAY_NIGHTS;
  }
  return Math.min(MAX_NIGHTS, Math.max(MIN_NIGHTS, lowerBound));
}

function getCheckInWithFlexible(range: DateRange): Pick<StayDates, 'checkIn' | 'flexible'> {
  const from = parseDate(range.from);
  const to = parseDate(range.to);
  const flexible = clampFlexible(range.daysFlexible);

  if (flexible > 0) {
    const checkIn = from > getMinDate() ? addDaysToDate(from, flexible) : addDaysToDate(to, -flexible);
    return { checkIn: toISO(checkIn), flexible };
  }

  const windowDays = getDateDifference(to, from, 'days');
  if (windowDays === 0) {
    return { checkIn: toISO(from), flexible: 0 };
  }

  const windowFlexible = Math.min(MAX_FLEXIBLE, Math.floor(windowDays / 2));
  return { checkIn: toISO(addDaysToDate(from, windowFlexible)), flexible: windowFlexible };
}

export function toStayDates(criteria: StayCriteria): StayDates {
  const { checkIn, flexible } = getCheckInWithFlexible(normalizeDateFrom(criteria.dateRange));
  const latestCheckIn = addDaysToDate(getMaxDate(), -MIN_NIGHTS);
  const boundedCheckIn = clampDate(parseDate(checkIn), getMinDate(), latestCheckIn);
  const nightsUntilMaxDate = getDateDifference(getMaxDate(), boundedCheckIn, 'days');

  return {
    checkIn: toISO(boundedCheckIn),
    nights: Math.min(getStayNights(criteria.nights), nightsUntilMaxDate),
    flexible,
  };
}

export function fromStayDates(stay: StayDates): StayCriteria {
  const checkIn = parseDate(stay.checkIn);
  const latestWindowEnd = addDaysToDate(getMaxDate(), -stay.nights);
  const fromIso = toISO(clampDate(addDaysToDate(checkIn, -stay.flexible)));
  const toIso = toISO(clampDate(addDaysToDate(checkIn, stay.flexible), getMinDate(), latestWindowEnd));

  return {
    dateRange: { from: fromIso, to: toIso, daysFlexible: fromIso === toIso ? 0 : stay.flexible },
    nights: { from: stay.nights, to: stay.nights },
  };
}

export function normalizeStayCriteria<T extends StayCriteria>(criteria: T): T {
  return { ...criteria, ...fromStayDates(toStayDates(criteria)) };
}
